import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Statistic, Button, Typography, Space, List, Tag, Empty, Spin, Badge, Divider } from 'antd';
import {
  CarOutlined,
  FileTextOutlined,
  ToolOutlined,
  CalendarOutlined,
  LogoutOutlined,
  UserOutlined,
  HistoryOutlined,
  FileDoneOutlined,
  GiftOutlined,
  BellOutlined,
  SettingOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { customerPortalApi } from '../services/apiInstances';
import PublicLayout from '../components/landing/PublicLayout';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';

const { Title, Text } = Typography;

const CustomerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, customerData, logout, accessToken } = useCustomerAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [rewardBalance, setRewardBalance] = useState<any>(null);

  const loadDashboard = React.useCallback(async () => {
    setLoading(true);
    try {
      const [dashboardRes, historyRes, rewardRes] = await Promise.all([
        customerPortalApi.get('/customer-portal/dashboard'),
        customerPortalApi.get('/customer-portal/service-history'),
        customerPortalApi.get('/rewards/balance').catch(() => ({ data: null }))
      ]);

      setDashboardData(dashboardRes.data);
      setRecentActivity(historyRes.data.serviceHistory.slice(0, 5));
      setRewardBalance(rewardRes.data);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/customer/login');
      return;
    }
    loadDashboard();
  }, [isAuthenticated, navigate, loadDashboard]);

  const handleLogout = async () => {
    await logout();
    navigate('/customer/login');
  };

  if (loading) {
    return (
      <PublicLayout>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <Spin size="large" />
        </div>
      </PublicLayout>
    );
  }

  const customer = customerData?.customer;

  return (
    <PublicLayout>
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f0f2f5 0%, #ffffff 100%)',
        padding: '40px 20px'
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {/* Header */}
          <Card style={{ marginBottom: 24 }}>
            <Row align="middle" justify="space-between">
              <Col>
                <Space direction="vertical" size={0}>
                  <Title level={3} style={{ margin: 0 }}>
                    <UserOutlined /> Welcome back, {customer?.first_name || 'Customer'}!
                  </Title>
                  <Text type="secondary">
                    {customer?.email} | {customer?.phone}
                  </Text>
                </Space>
              </Col>
              <Col>
                <Button icon={<LogoutOutlined />} onClick={handleLogout}>
                  Logout
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Stats */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="My Vehicles"
                  value={dashboardData?.vehicles || 0}
                  prefix={<CarOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Estimates"
                  value={dashboardData?.estimates?.total || 0}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Work Orders"
                  value={dashboardData?.workOrders?.total || 0}
                  prefix={<ToolOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Appointments"
                  value={dashboardData?.upcomingAppointments || 0}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Rewards Summary Card */}
          {rewardBalance && (
            <Card
              style={{
                marginBottom: 24,
                background: 'linear-gradient(135deg, #faad14 0%, #faad1440 100%)',
                borderColor: '#faad14',
              }}
            >
              <Row align="middle" justify="space-between">
                <Col>
                  <Space direction="vertical" size={0}>
                    <Space>
                      <GiftOutlined style={{ fontSize: 20, color: '#faad14' }} />
                      <Title level={4} style={{ margin: 0 }}>
                        {rewardBalance.points?.toLocaleString() || 0} Points
                      </Title>
                      <Badge
                        color="#faad14"
                        text={
                          rewardBalance.tier
                            ? rewardBalance.tier.charAt(0).toUpperCase() + rewardBalance.tier.slice(1)
                            : 'Bronze'
                        }
                      />
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {rewardBalance.pointsToNextTier || 0} points until next tier
                    </Text>
                  </Space>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    icon={<GiftOutlined />}
                    onClick={() => navigate('/customer/rewards')}
                  >
                    View Rewards
                  </Button>
                </Col>
              </Row>
            </Card>
          )}

          {/* Quick Actions */}
          <Card title="Quick Actions" style={{ marginBottom: 24 }}>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={6}>
                <Button
                  block
                  size="large"
                  icon={<CalendarOutlined />}
                  onClick={() => navigate('/book')}
                >
                  Book Service
                </Button>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Button
                  block
                  size="large"
                  icon={<CarOutlined />}
                  onClick={() => navigate('/customer/vehicles')}
                >
                  My Vehicles
                </Button>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Button
                  block
                  size="large"
                  icon={<FileTextOutlined />}
                  onClick={() => navigate('/customer/estimates')}
                >
                  Estimates
                </Button>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Button
                  block
                  size="large"
                  icon={<HistoryOutlined />}
                  onClick={() => navigate('/customer/history')}
                >
                  Service History
                </Button>
              </Col>
              {/* WP-13 slice 1. Same breakpoints as the four above so the
                  existing row is untouched and this simply wraps. */}
              <Col xs={24} sm={12} md={6}>
                <Button
                  block
                  size="large"
                  icon={<FileDoneOutlined />}
                  onClick={() => navigate('/customer/receipts')}
                >
                  My Receipts
                </Button>
              </Col>
            </Row>
          </Card>

          <Row gutter={[16, 16]}>
            {/* Recent Activity */}
            <Col xs={24} lg={14}>
              <Card
                title={
                  <Space>
                    <HistoryOutlined />
                    <span>Recent Service History</span>
                  </Space>
                }
                extra={
                  <Button type="link" onClick={() => navigate('/customer/history')}>
                    View All
                  </Button>
                }
              >
                {recentActivity.length > 0 ? (
                  <List
                    dataSource={recentActivity}
                    renderItem={(item: any) => (
                      <List.Item>
                        <List.Item.Meta
                          avatar={
                            item.type === 'estimate' ? (
                              <FileTextOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                            ) : (
                              <ToolOutlined style={{ fontSize: 24, color: '#722ed1' }} />
                            )
                          }
                          title={
                            <Space>
                              <Text strong>{item.number}</Text>
                              <Tag color={item.status === 'completed' ? 'green' : 'blue'}>
                                {item.status}
                              </Tag>
                            </Space>
                          }
                          description={
                            <Space direction="vertical" size={0}>
                              <Text type="secondary">
                                {item.year} {item.make} {item.model}
                              </Text>
                              <Text type="secondary">
                                {dayjs(item.created_at).format('MMM D, YYYY')}
                              </Text>
                            </Space>
                          }
                        />
                        <div>
                          <Text strong style={{ fontSize: 16 }}>
                            ${Number(item.total_amount || 0).toFixed(2)}
                          </Text>
                        </div>
                      </List.Item>
                    )}
                  />
                ) : (
                  <Empty
                    description="No service history yet"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              </Card>
            </Col>

            {/* Account Info */}
            <Col xs={24} lg={10}>
              <Card title="Account Information">
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <div>
                    <Text type="secondary">Name</Text>
                    <br />
                    <Text strong>
                      {customer?.company_name || `${customer?.first_name} ${customer?.last_name}`}
                    </Text>
                  </div>

                  <Divider style={{ margin: '8px 0' }} />

                  <div>
                    <Text type="secondary">Email</Text>
                    <br />
                    <Text strong>{customer?.email}</Text>
                  </div>

                  <Divider style={{ margin: '8px 0' }} />

                  <div>
                    <Text type="secondary">Phone</Text>
                    <br />
                    <Text strong>{customer?.phone}</Text>
                  </div>

                  <Divider style={{ margin: '8px 0' }} />

                  <div>
                    <Text type="secondary">Customer Since</Text>
                    <br />
                    <Text strong>
                      {customer?.created_at
                        ? dayjs(customer.created_at).format('MMMM YYYY')
                        : 'N/A'}
                    </Text>
                  </div>

                  {dashboardData?.lastService && (
                    <>
                      <Divider style={{ margin: '8px 0' }} />
                      <div>
                        <Text type="secondary">Last Service</Text>
                        <br />
                        <Text strong>
                          {dayjs(dashboardData.lastService).format('MMM D, YYYY')}
                        </Text>
                      </div>
                    </>
                  )}
                </Space>
              </Card>
            </Col>
          </Row>

          {/* Customer Portal Navigation */}
          <Card title="Customer Portal" style={{ marginTop: 24 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Card
                  hoverable
                  onClick={() => navigate('/customer/rewards')}
                  style={{ textAlign: 'center', cursor: 'pointer' }}
                >
                  <GiftOutlined style={{ fontSize: 32, color: '#faad14' }} />
                  <div style={{ marginTop: 12 }}>
                    <Text strong>Rewards</Text>
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card
                  hoverable
                  onClick={() => navigate('/customer/notifications')}
                  style={{ textAlign: 'center', cursor: 'pointer' }}
                >
                  <BellOutlined style={{ fontSize: 32, color: '#1890ff' }} />
                  <div style={{ marginTop: 12 }}>
                    <Text strong>Notifications</Text>
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card
                  hoverable
                  onClick={() => navigate('/customer/preferences')}
                  style={{ textAlign: 'center', cursor: 'pointer' }}
                >
                  <SettingOutlined style={{ fontSize: 32, color: '#52c41a' }} />
                  <div style={{ marginTop: 12 }}>
                    <Text strong>Preferences</Text>
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card
                  hoverable
                  onClick={() => navigate('/customer/profile')}
                  style={{ textAlign: 'center', cursor: 'pointer' }}
                >
                  <UserOutlined style={{ fontSize: 32, color: '#722ed1' }} />
                  <div style={{ marginTop: 12 }}>
                    <Text strong>My Profile</Text>
                  </div>
                </Card>
              </Col>
            </Row>
          </Card>
        </div>
      </div>
    </PublicLayout>
  );
};

export default CustomerDashboard;
